/**
 * SubscriptionInfoCard Component
 *
 * Displays subscription information and management actions.
 * Part of modularized UserAccountManagement component.
 *
 * Project: P07 - Subscription Flow
 * Sprint: S04 - Subscription Management & Edge Cases
 * Work Unit: WU01 - Subscription Settings UI
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, AlertCircle, ExternalLink } from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardBody,
  Button,
  Chip,
  Spinner,
  Alert
} from '@enfomentis/ui';
import { subscriptionsRepository } from '@/repositories/subscriptionsRepository';
import { useAuth } from '@/hooks/useAuth';
import { Plans } from '@enfomentis/shared/config';
import type {
  SubscriptionInfo,
  SubscriptionStatus,
  SubscriptionPlanType,
  SubscriptionBillingInterval
} from '@/types/supabase/Subscription';

// =============================================================================
// Type Definitions
// =============================================================================

type LoadingState = 'loading' | 'loaded' | 'error';

// =============================================================================
// Helper Functions
// =============================================================================

function getPlanDisplayName(planType: SubscriptionPlanType): string {
  const plan = Plans[planType];
  return plan?.name || 'Unknown Plan';
}

function getStatusColor(status: SubscriptionStatus): 'success' | 'warning' | 'danger' | 'default' {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'success';
    case 'past_due':
      return 'warning';
    case 'cancelled':
    case 'unpaid':
      return 'danger';
    default:
      return 'default';
  }
}

function getStatusLabel(status: SubscriptionStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'past_due':
      return 'Past Due';
    case 'cancelled':
      return 'Cancelled';
    case 'unpaid':
      return 'Unpaid';
    case 'trialing':
      return 'Trial';
    default:
      return 'Unknown';
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function getBillingLabel(interval: SubscriptionBillingInterval): string {
  return interval === 'year' ? 'Annual' : 'Monthly';
}

// =============================================================================
// Component
// =============================================================================

/**
 * Subscription Info Card Component
 *
 * Displays:
 * - Current plan (Pro or Pro+)
 * - Subscription status (active, past_due, cancelled, etc.)
 * - Billing interval (monthly/yearly)
 * - Next renewal/end date
 * - Cancel at period end notice
 * - Manage Subscription button (opens Stripe Customer Portal)
 */
export const SubscriptionInfoCard: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch subscription data via repository
  useEffect(() => {
    async function fetchSubscription() {
      if (!authState.user?.id) {
        setLoadingState('loaded');
        return;
      }

      try {
        const data = await subscriptionsRepository.getInfoByUserId(authState.user.id);
        setSubscription(data);
        setLoadingState('loaded');
      } catch (err) {
        console.error('Failed to fetch subscription:', err);
        setError('Failed to load subscription information');
        setLoadingState('error');
      }
    }

    fetchSubscription();
  }, [authState.user?.id]);

  // Open Stripe Customer Portal
  const handleManageSubscription = useCallback(async () => {
    if (!authState.sessionToken) {
      setError('Not authenticated');
      return;
    }

    setPortalLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/create-billing-portal-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authState.sessionToken}`,
          },
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create portal session');
      }

      // Redirect to Stripe Customer Portal
      window.location.href = result.portalUrl;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  }, [authState.sessionToken]);

  // Loading state
  if (loadingState === 'loading') {
    return (
      <Card shadow="sm">
        <CardBody className="p-4 sm:p-6">
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
          </div>
        </CardBody>
      </Card>
    );
  }

  // Error state
  if (loadingState === 'error') {
    return (
      <Card shadow="sm">
        <CardBody className="p-4 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="text-danger shrink-0">
              <AlertCircle size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-text-primary">
                Subscription
              </h3>
              <Alert color="danger" variant="flat" className="mt-2">
                {error || 'Failed to load subscription information'}
              </Alert>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  // No subscription state
  if (!subscription) {
    return (
      <Card shadow="sm">
        <CardBody className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="text-primary shrink-0">
                <CreditCard size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-text-primary">
                  Subscription
                </h3>
                <p className="text-text-secondary">
                  No active subscription
                </p>
              </div>
            </div>
            <Button
              color="primary"
              size="lg"
              onPress={() => navigate('/subscribe')}
              className="w-full sm:w-auto"
            >
              Subscribe Now
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Active subscription display
  const planName = getPlanDisplayName(subscription.planType);
  const statusColor = getStatusColor(subscription.status);
  const statusLabel = getStatusLabel(subscription.status);
  const billingLabel = getBillingLabel(subscription.billingInterval);

  return (
    <Card shadow="sm">
      <CardBody className="p-4 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="text-primary shrink-0">
            <CreditCard size={24} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h3 className="text-lg font-semibold text-text-primary">
                Subscription
              </h3>
              <Button
                variant="bordered"
                size="md"
                endContent={<ExternalLink size={16} />}
                onPress={handleManageSubscription}
                isLoading={portalLoading}
                className="w-full sm:w-auto"
              >
                Manage Subscription
              </Button>
            </div>

            {/* Error message */}
            {error && (
              <Alert color="danger" variant="flat" className="mb-4">
                {error}
              </Alert>
            )}

            {/* Subscription details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-text-primary">Plan</p>
                <p className="text-text-secondary">{planName}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Status</p>
                <Chip color={statusColor} size="sm" variant="flat">
                  {statusLabel}
                </Chip>
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Billing</p>
                <p className="text-text-secondary">{billingLabel}</p>
              </div>
              {subscription.currentPeriodEnd && (
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    {subscription.cancelAtPeriodEnd ? 'Access Until' : 'Next Renewal'}
                  </p>
                  <p className="text-text-secondary">
                    {formatDate(subscription.currentPeriodEnd)}
                  </p>
                </div>
              )}
            </div>

            {/* Cancel at period end notice */}
            {subscription.cancelAtPeriodEnd && subscription.status === 'active' && (
              <Alert color="warning" variant="flat" className="mt-4">
                Your subscription is set to cancel at the end of the billing period.
                You will retain access until {subscription.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : 'the end of your billing period'}.
              </Alert>
            )}

            {/* Past due warning */}
            {subscription.status === 'past_due' && (
              <Alert color="warning" variant="flat" className="mt-4">
                Your payment is past due. Please update your payment method to continue your subscription.
              </Alert>
            )}

            {/* Cancelled/Unpaid notice */}
            {(subscription.status === 'cancelled' || subscription.status === 'unpaid') && (
              <Alert color="danger" variant="flat" className="mt-4">
                Your subscription has been {subscription.status === 'cancelled' ? 'cancelled' : 'suspended due to non-payment'}.
                {subscription.currentPeriodEnd && subscription.status === 'cancelled' && (
                  <> You have access until {formatDate(subscription.currentPeriodEnd)}.</>
                )}
              </Alert>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
